import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../ui/utils";
import type { DriftHistoryPoint } from "../../../api/frameworksApi";

/**
 * Every scan a control has had, placed by the day it ran and the time of day.
 *
 * <h3>Why the axes are what they are</h3>
 * A verdict is categorical, so it cannot be an axis. A timestamp, though, is two quantities — which
 * day, and when within it — and splitting them is what makes a scan schedule legible: a control
 * scanned at 04:00, 10:00, 16:00 and 22:00 shows four vertical bands, and a scan that ran late
 * stands out of line rather than disappearing into a sequence.
 *
 * <h3>Rows are the days that exist, not the days that elapsed</h3>
 * `days` is built from the distinct dates actually present, so a fortnight with no scans occupies
 * no vertical space. That is the difference between this and a plain time axis, and it is why a
 * sparse history stays readable.
 *
 * <h3>Presentational only</h3>
 * Lifted out of the Drift Management screen so the AI companion can render the same picture rather
 * than an approximation of it. Selection is a prop and a callback, not internal state, so the
 * screen keeps its evidence drill-down while the chat panel — which has nowhere to put one — simply
 * does not pass a handler.
 */

/** One plotted scan, in the shape the drift screen's detail list already consumes. */
export interface ScanPoint {
  /** Minutes past UTC midnight — the horizontal position. */
  x: number;
  /** Row index among the days present — the vertical position. */
  y: number;
  status: string;
  raw: DriftHistoryPoint;
  /** Scans within the batch window of this one, for a drill-down that fetches per batch. */
  relatedRaw?: DriftHistoryPoint[];
  dayLabel: string;
}

/**
 * Scans landing within five minutes of each other are one assessment run, not several. Evidence is
 * fetched per run, so the grouping has to match how it is stored.
 */
export const SCAN_BATCH_WINDOW_MS = 5 * 60 * 1000;

export function scanTimeMs(point: DriftHistoryPoint): number {
  const ms = new Date(point.timestamp).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

export function groupIntoRuns(points: DriftHistoryPoint[]): DriftHistoryPoint[][] {
  const runs: DriftHistoryPoint[][] = [];
  for (const p of points) {
    const last = runs[runs.length - 1];
    if (last && scanTimeMs(p) - scanTimeMs(last[0]) <= SCAN_BATCH_WINDOW_MS) {
      last.push(p);
    } else {
      runs.push([p]);
    }
  }
  return runs;
}

/** "Jul 14" — UTC, zero-padded, so rows sort and align by width. */
export function fmtDay(iso: string): string {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return `${d.toLocaleString("en-US", { month: "short" })} ${String(
      d.getUTCDate(),
    ).padStart(2, "0")}`;
  } catch {
    return iso;
  }
}

const TOTAL_MINUTES = 24 * 60;
const PLOT_LEFT_PAD = 28;
const RIGHT_MARGIN = 16;
const TOP_MARGIN = 44;
const BAR_HEIGHT = 32;

const TIMELINE_FALLBACK_ID = "scan-timeline";

/** Shared by both SVGs, so the axis and the rows label themselves the same way. */
const TIMELINE_LABEL_CSS = `
  .drift-tick-label { fill: #64748b; }
  .drift-day-label  { fill: #334155; }
  .dark .drift-tick-label { fill: #94a3b8; }
  .dark .drift-day-label  { fill: #e2e8f0; }
`;

/** The two verdicts the legend offers, and the diamond fills they correspond to. */
const LEGEND_ENTRIES = [
  { status: "passed" as const, label: "Pass", color: "#2ecc71" },
  { status: "failed" as const, label: "Fail", color: "#f4845f" },
];

/** Below this the hour labels collide, so the caller's interval is forced wider. */
const MIN_PX_PER_HOUR_LABEL = 22;

/**
 * Smallest half-width a scan stays reachable at.
 *
 * The mark is the hover target, and the tooltip is the only thing that names a scan — neither axis
 * carries a date. So a caller may shrink the painted diamond as far as it likes; the target under
 * it does not follow past this.
 */
const MIN_HIT_RADIUS = 9;

export function toDomToken(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/[^A-Za-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

interface RunTooltip {
  passPoint: ScanPoint | null;
  failPoint: ScanPoint | null;
  dayLabel: string;
}

interface TooltipState extends RunTooltip {
  mouseX: number;
  mouseY: number;
}

function fmtTimeSec(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--:--";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}`;
}

function opaCountOf(records: DriftHistoryPoint[]): number {
  return records.reduce((n, r) => n + (r.opaDetails?.length ?? 0), 0);
}

export interface ScanTimelineMatrixProps {
  history: DriftHistoryPoint[];
  /** Dims every scan that does not match, for cross-filtering from a KPI card. */
  highlightStatus?: "passed" | "failed" | "drift" | null;
  id?: string;
  name?: string;
  /**
   * Opens a scan, or closes the open one when the background is clicked ({@code null}). Omit it and
   * the matrix is read-only: no halo, no "Read more", no click target — which is what the chat
   * panel wants, having nowhere to open a drill-down into.
   */
  onPointClick?: (point: ScanPoint | null) => void;
  /** Timestamp of the open scan, so the halo can be driven from outside. */
  selectedTimestamp?: string | null;

  /** Label every Nth hour. Raised automatically when the column is too narrow for the request. */
  hourTickInterval?: number;
  /** Width reserved for the day labels. */
  labelWidth?: number;
  rowHeight?: number;
  /**
   * Half-width of a scan diamond. The default suits the screen's 64px rows; a narrower column with
   * shorter rows wants less. Emphasis for a matched or selected scan is applied on top of this, so
   * it stays a step above its neighbours rather than a fixed size.
   */
  markRadius?: number;
  /**
   * Grows a mark by a pixel while a status filter matches it.
   *
   * <p>On by default, as the drift screen has it. Worth turning off where the marks are already
   * small: dimming everything else has said which ones matched, and growing them says it twice.
   */
  emphasiseMatches?: boolean;

  /**
   * Caps the height of the rows, scrolling them within it. Unset means the chart grows to fit the
   * days it has, which is right on a page and wrong in a chat card — twenty days at this row height
   * is most of a screen.
   *
   * <p>The hour axis is drawn outside the scrolled area whenever this is set, so scrolling to an
   * older date does not take away the reference that says what time of day a mark sits at.
   */
  maxHeight?: number;

  /**
   * Turns the legend into Pass / Fail filter buttons.
   *
   * <p>Off by default because the drift screen already filters from its KPI cards, and two controls
   * driving one filter would have to be kept in sync. Where there are no such cards — the chat
   * panel — this is the only way to isolate failures.
   */
  interactiveLegend?: boolean;
}

export function ScanTimelineMatrix({
  history,
  highlightStatus,
  id: idProp,
  name: nameProp,
  onPointClick,
  selectedTimestamp,
  hourTickInterval = 1,
  labelWidth = 88,
  rowHeight = 64,
  markRadius = 8,
  emphasiseMatches = true,
  maxHeight,
  interactiveLegend = false,
}: ScanTimelineMatrixProps) {
  const id = toDomToken(idProp) || TIMELINE_FALLBACK_ID;
  const name = String(nameProp ?? "").trim() || id;
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [legendFilter, setLegendFilter] = useState<"passed" | "failed" | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(700);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A caller driving the prop keeps control; the legend's own state is only consulted when nobody
  // else is steering. In practice they never collide — the screen drives the prop and does not
  // enable the legend, the chat enables the legend and never drives the prop.
  const activeStatus = highlightStatus ?? (interactiveLegend ? legendFilter : null);

  function toggleLegend(status: "passed" | "failed") {
    setLegendFilter((prev) => (prev === status ? null : status));
  }

  function scheduleHide() {
    hideTimer.current = setTimeout(() => setTooltip(null), 150);
  }
  function cancelHide() {
    if (hideTimer.current) clearTimeout(hideTimer.current);
  }

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const sortedHistory = useMemo(
    () =>
      (Array.isArray(history) ? history : [])
        .filter((h) => h && !Number.isNaN(new Date(h.timestamp).getTime()))
        .sort((a, b) => a.timestamp.localeCompare(b.timestamp)),
    [history],
  );

  const scanDomIds = useMemo(() => {
    const used = new Set<string>();
    const map = new Map<DriftHistoryPoint, string>();
    sortedHistory.forEach((point, index) => {
      const base = toDomToken(point.timestamp) || `scan-${index}`;
      let candidate = base;
      let suffix = 2;
      while (used.has(candidate)) {
        candidate = `${base}-${suffix}`;
        suffix += 1;
      }
      used.add(candidate);
      map.set(point, candidate);
    });
    return map;
  }, [sortedHistory]);

  // Timestamps involved in a drift event - the point before a status flip (last
  // passed/failed) and the point it flipped to. Used to highlight drift only.
  const driftTimestamps = useMemo(() => {
    const s = new Set<string>();
    for (let i = 1; i < sortedHistory.length; i++) {
      if (sortedHistory[i].status !== sortedHistory[i - 1].status) {
        s.add(sortedHistory[i - 1].timestamp);
        s.add(sortedHistory[i].timestamp);
      }
    }
    return s;
  }, [sortedHistory]);

  const { days, pointsByDay } = useMemo(() => {
    const dayMap = new Map<string, DriftHistoryPoint[]>();
    sortedHistory.forEach((h) => {
      const ymd = h.timestamp.slice(0, 10);
      if (!dayMap.has(ymd)) dayMap.set(ymd, []);
      dayMap.get(ymd)!.push(h);
    });
    const dayList = Array.from(dayMap.keys()).sort();
    return { days: dayList, pointsByDay: dayMap };
  }, [sortedHistory]);

  if (days.length === 0) {
    return (
      <p
        id={id}
        data-name={name}
        className="text-sm text-gray-500 text-center py-8"
      >
        No scan data available.
      </p>
    );
  }

  const plotW = width - labelWidth - PLOT_LEFT_PAD - RIGHT_MARGIN;
  const plotStartX = labelWidth + PLOT_LEFT_PAD;
  const svgH = TOP_MARGIN + days.length * rowHeight + 40;

  // Twenty-four labels need roughly 1350px. In a chat column there is a fifth of that, so the
  // requested interval is a floor, not a promise — the axis widens it until the labels fit rather
  // than letting them overlap into a smear.
  const hourStep = Math.max(
    1,
    hourTickInterval,
    Math.ceil(MIN_PX_PER_HOUR_LABEL / Math.max(1, plotW / 24)),
  );
  const hourTicks = Array.from({ length: 24 }, (_, i) => i);

  // Capping the height splits the chart in two: the hour axis above the scroll box, the rows
  // inside it. Without a cap everything stays in one SVG, exactly as the drift screen has it.
  const scrolls = typeof maxHeight === "number";
  const bodyH = days.length * rowHeight;
  const rowTop = scrolls ? 0 : TOP_MARGIN;
  const gridTop = scrolls ? 0 : TOP_MARGIN - 10;
  const gridBottom = scrolls ? bodyH : svgH - 30;

  /** Vertical hour gridlines, drawn into whichever SVG is asking. */
  function hourGridLines(y1: number, y2: number) {
    return hourTicks.map((h) => (
      <line
        key={h}
        x1={minToX(h * 60)}
        y1={y1}
        x2={minToX(h * 60)}
        y2={y2}
        stroke="#2d3748"
        strokeDasharray="3 3"
      />
    ));
  }

  /** Hour numbers. A gridline every hour, a label only where one fits. */
  function hourLabels() {
    return hourTicks
      .filter((h) => h % hourStep === 0)
      .map((h) => (
        <text
          key={h}
          x={minToX(h * 60)}
          y={TOP_MARGIN - 14}
          textAnchor="middle"
          className="drift-tick-label"
          fontSize={12}
          fontWeight={600}
        >
          {String(h).padStart(2, "0")}
        </text>
      ));
  }

  function minToX(min: number) {
    return plotStartX + (min / TOTAL_MINUTES) * plotW;
  }

  function handleBarMouseEnter(
    e: React.MouseEvent<SVGElement>,
    run: RunTooltip,
  ) {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    setTooltip({
      ...run,
      mouseX: e.clientX - rect.left,
      mouseY: e.clientY - rect.top,
    });
  }

  return (
    <div id={id} data-name={name} ref={containerRef} className="relative">
      {/*
        This legend is deliberately its own, not the shared SeriesLegend. It was briefly restyled to
        match that one, which also changed the Drift Management screen — a screen nobody had asked
        to change, and the larger type reads better on both. So the appearance stays as it was; only
        the click behaviour is new, and that is opt-in.
      */}
      <div id={`${id}-legend`} data-name={`${name}-legend`} className="flex items-center gap-4 mb-3 text-sm">
        {LEGEND_ENTRIES.map(({ status, label, color }) => {
          const swatch = (
            <svg width={13} height={13} viewBox="0 0 12 12" aria-hidden="true">
              <polygon points="6,0 12,6 6,12 0,6" fill={color} />
            </svg>
          );
          const text = (
            <span className="font-medium text-gray-700 dark:text-gray-300">{label}</span>
          );
          return interactiveLegend ? (
            <button
              key={status}
              type="button"
              // A filter, so it announces itself as one. This is the only route to it in the chat.
              aria-pressed={legendFilter === status}
              onClick={() => toggleLegend(status)}
              className={cn(
                "flex items-center gap-1.5 rounded-md px-1.5 py-0.5 transition-opacity",
                "hover:bg-foreground/5",
                legendFilter && legendFilter !== status ? "opacity-45" : "opacity-100",
              )}
            >
              {swatch}
              {text}
            </button>
          ) : (
            <div key={status} className="flex items-center gap-1.5">
              {swatch}
              {text}
            </div>
          );
        })}
      </div>

      {/*
        When the rows scroll, the hour axis is drawn in its own SVG above the scroll box. It cannot
        be `position: sticky` — that does not apply inside an SVG — and leaving it in the scrolled
        content would mean scrolling to an older date takes away the reference that says what time
        of day a mark sits at. Both SVGs share `width` and `minToX`, so their gridlines line up by
        construction rather than by tuning.
      */}
      {scrolls && (
        <svg
          width={width}
          height={TOP_MARGIN}
          aria-hidden="true"
          style={{ display: "block" }}
        >
          <style>{TIMELINE_LABEL_CSS}</style>
          {hourGridLines(TOP_MARGIN - 10, TOP_MARGIN)}
          {hourLabels()}
        </svg>
      )}

      <div
        style={
          scrolls
            ? { maxHeight, overflowY: "auto", position: "relative" }
            : undefined
        }
      >
        <svg
          id={`${id}-plot`}
          data-name={`${name}-plot`}
          role="img"
          aria-label={`Finding timeline: ${sortedHistory.length} assessment ${sortedHistory.length === 1 ? "run" : "runs"} plotted by day and time of day in UTC`}
          width={width}
          height={scrolls ? bodyH : svgH}
          style={{ display: "block" }}
          onMouseLeave={scheduleHide}
          onClick={onPointClick ? () => onPointClick(null) : undefined}
        >
          <style>{TIMELINE_LABEL_CSS}</style>
          {/* X-axis grid + labels - tick "h" drawn where hour h UTC lives */}
          {hourGridLines(gridTop, gridBottom)}
          {/* End-of-day marker (24:00) - unlabeled boundary line */}
          <line
            x1={minToX(24 * 60)}
            y1={gridTop}
            x2={minToX(24 * 60)}
            y2={gridBottom}
            stroke="#2d3748"
            strokeDasharray="3 3"
          />

          {/* Both live in the header when the rows scroll. */}
          {!scrolls && hourLabels()}
          {!scrolls && (
            <text
              x={plotStartX + plotW / 2}
              y={svgH - 8}
              textAnchor="middle"
              className="drift-tick-label"
              fontSize={12}
              fontWeight={600}
            >
              Time of day (UTC)
            </text>
          )}

          {/* Rows */}
          {days.map((ymd, dayIdx) => {
            const rowY = rowTop + dayIdx * rowHeight;
            const barY = rowY + (rowHeight - BAR_HEIGHT) / 2;
            const dayLabel = fmtDay(ymd);
            // Sort scans by time within the day so we can compute next-scan end time
            const pts = [...(pointsByDay.get(ymd) || [])].sort((a, b) => {
              const aMin =
                new Date(a.timestamp).getUTCHours() * 60 +
                new Date(a.timestamp).getUTCMinutes();
              const bMin =
                new Date(b.timestamp).getUTCHours() * 60 +
                new Date(b.timestamp).getUTCMinutes();
              return aMin - bMin;
            });

            const cy = barY + BAR_HEIGHT / 2;
            const isAlt = dayIdx % 2 === 1;
            return (
              <g key={ymd}>
                {/* Alternating row background band for better separation */}
                {isAlt && (
                  <rect
                    x={plotStartX}
                    y={rowY}
                    width={plotW}
                    height={rowHeight}
                    fill="#ffffff"
                    fillOpacity={0.015}
                  />
                )}

                <text
                  x={labelWidth - 12}
                  y={cy + 4}
                  textAnchor="end"
                  className="drift-day-label"
                  fontSize={13}
                  fontWeight={600}
                >
                  {dayLabel}
                </text>

                {/* Horizontal grid line for this row */}
                <line
                  x1={plotStartX}
                  y1={cy}
                  x2={plotStartX + plotW}
                  y2={cy}
                  stroke="#2d3748"
                  strokeDasharray="2 4"
                  strokeOpacity={0.5}
                />

                {groupIntoRuns(pts).map((run, i) => {
                  const first = run[0];
                  const dt = new Date(first.timestamp);
                  const startMin = dt.getUTCHours() * 60 + dt.getUTCMinutes();
                  const cx = minToX(startMin);
                  const isPass = (p: DriftHistoryPoint) =>
                    (p.status ?? "").toLowerCase() === "passed";
                  const passPts = run.filter(isPass);
                  const failPts = run.filter((p) => !isPass(p));
                  const hasPass = passPts.length > 0;
                  const hasFail = failPts.length > 0;
                  const mixed = hasPass && hasFail;

                  const isMatch =
                    !activeStatus ||
                    run.some((p) =>
                      activeStatus === "drift"
                        ? driftTimestamps.has(p.timestamp)
                        : p.status === activeStatus,
                    );
                  const isSelected = run.some(
                    (p) => p.timestamp === selectedTimestamp,
                  );
                  const excluded = !!activeStatus && !isMatch && !isSelected;
                  if (excluded) return null;
                  const r = isSelected
                    ? markRadius + 2
                    : isMatch && activeStatus && emphasiseMatches
                      ? markRadius + 1
                      : markRadius;
                  const hitR = Math.max(MIN_HIT_RADIUS, r);

                  const GREEN = "#2ecc71";
                  const ORANGE = "#f4845f";
                  const full = `${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`;
                  const leftHalf = `${cx},${cy - r} ${cx},${cy + r} ${cx - r},${cy}`;
                  const rightHalf = `${cx},${cy - r} ${cx},${cy + r} ${cx + r},${cy}`;
                  const hitPoints = `${cx},${cy - hitR} ${cx + hitR},${cy} ${cx},${cy + hitR} ${cx - hitR},${cy}`;

                  const mkPoint = (
                    subset: DriftHistoryPoint[],
                    status: string,
                  ): ScanPoint => ({
                    x: startMin,
                    y: dayIdx,
                    status,
                    raw: subset[0],
                    relatedRaw: subset,
                    dayLabel,
                  });
                  const passPoint = hasPass ? mkPoint(passPts, "passed") : null;
                  const failPoint = hasFail ? mkPoint(failPts, "failed") : null;
                  const combinedPoint = mkPoint(run, hasFail ? "failed" : "passed");
                  const runTooltip: RunTooltip = { passPoint, failPoint, dayLabel };

                  const strokeColor = isSelected
                    ? "#818cf8"
                    : isMatch && activeStatus
                      ? hasFail
                        ? "#dc2626"
                        : "#16a34a"
                      : "var(--diamond-stroke, #0d1117)";
                  const strokeW = isSelected ? 2.5 : isMatch && activeStatus ? 2 : 1.5;
                  const opacity = activeStatus && !isMatch && !isSelected ? 0.2 : 1;

                  const handleClick = onPointClick
                    ? (e: React.MouseEvent<SVGElement>) => {
                        e.stopPropagation();
                        onPointClick(mixed ? combinedPoint : passPoint ?? failPoint);
                        setTooltip(null);
                      }
                    : undefined;

                  return (
                    <g key={i}>
                      {isSelected && (
                        <circle cx={cx} cy={cy} fill="none" stroke="#818cf8">
                          <animate attributeName="r" values={`${r + 4};${r + 10};${r + 4}`} dur="1.6s" repeatCount="indefinite" />
                          <animate attributeName="opacity" values="0.9;0.2;0.9" dur="1.6s" repeatCount="indefinite" />
                          <animate attributeName="stroke-width" values="2.5;1;2.5" dur="1.6s" repeatCount="indefinite" />
                        </circle>
                      )}

                      {mixed ? (
                        <g style={{ opacity, pointerEvents: "none" }}>
                          <polygon points={leftHalf} fill={GREEN} />
                          <polygon points={rightHalf} fill={ORANGE} />
                          <polygon points={full} fill="none" stroke={strokeColor} strokeWidth={strokeW} strokeLinejoin="round" />
                        </g>
                      ) : (
                        <polygon
                          points={full}
                          fill={hasPass ? GREEN : ORANGE}
                          stroke={strokeColor}
                          strokeWidth={strokeW}
                          opacity={opacity}
                          style={{ pointerEvents: "none", transition: "opacity 0.2s, stroke-width 0.2s" }}
                        />
                      )}

                      <polygon
                        id={`${id}-scan-${scanDomIds.get(first) ?? `${toDomToken(ymd)}-${i}`}`}
                        data-name={`${name}-scan`}
                        data-status={mixed ? "mixed" : first.status ?? "unknown"}
                        data-timestamp={first.timestamp ?? ""}
                        points={hitPoints}
                        fill="transparent"
                        style={{
                          cursor: onPointClick ? "pointer" : "default",
                          pointerEvents: excluded ? "none" : undefined,
                        }}
                        onClick={handleClick}
                        onMouseEnter={(e) => handleBarMouseEnter(e, runTooltip)}
                        onMouseMove={(e) => {
                          const container = containerRef.current;
                          if (!container) return;
                          const rect = container.getBoundingClientRect();
                          setTooltip((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  mouseX: e.clientX - rect.left,
                                  mouseY: e.clientY - rect.top,
                                }
                              : prev,
                          );
                        }}
                      />
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>
      </div>

      {scrolls && (
        <p className="mt-1 text-center text-[12px] font-semibold text-muted-foreground">
          Time of day (UTC)
        </p>
      )}

      {tooltip &&
        (() => {
          const tipX = Math.min(tooltip.mouseX + 12, width - 240);
          const tipY = tooltip.mouseY - 10;
          const section = (
            point: ScanPoint,
            passed: boolean,
          ) => {
            const count = opaCountOf(point.relatedRaw ?? [point.raw]);
            const time = fmtTimeSec(point.raw.timestamp);
            return (
              <div>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${passed ? "bg-green-400" : "bg-red-400"}`} />
                  <span className={`text-xs font-semibold ${passed ? "text-green-400" : "text-red-400"}`}>
                    {passed ? "Passed" : "Failed"}
                  </span>
                  <span className="text-[11px] text-gray-400 ml-auto">{time} UTC</span>
                </div>
                {count > 0 && (
                  <p className="text-[11px] text-gray-500 mt-1">
                    {count} automated result{count !== 1 ? "s" : ""}
                  </p>
                )}
                {onPointClick && (
                  <button
                    className="mt-1 flex items-center gap-1 text-[11px] text-indigo-400 hover:text-indigo-300 font-medium transition-colors"
                    onClick={() => {
                      onPointClick(point);
                      setTooltip(null);
                    }}
                  >
                    Read more <span className="text-[10px]">›</span>
                  </button>
                )}
              </div>
            );
          };
          return (
            <div
              className="absolute z-30"
              style={{ left: tipX, top: tipY }}
              onMouseEnter={cancelHide}
              onMouseLeave={() => setTooltip(null)}
            >
              <div className="bg-[#0d0f1a] border border-white/[0.12] rounded-xl shadow-2xl p-3 w-56 space-y-2">
                <div className="flex items-center justify-end">
                  <span className="text-xs text-gray-500">{tooltip.dayLabel}</span>
                </div>
                {tooltip.passPoint && section(tooltip.passPoint, true)}
                {tooltip.passPoint && tooltip.failPoint && (
                  <div className="border-t border-white/[0.08]" />
                )}
                {tooltip.failPoint && section(tooltip.failPoint, false)}
              </div>
            </div>
          );
        })()}
    </div>
  );
}
